import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withDevtools,
} from '@angular-architects/ngrx-toolkit';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {
  removeAllEntities,
  setAllEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { distinctUntilChanged, exhaustMap, pipe, tap } from 'rxjs';

import { Overview, OverviewFilters, OverviewService } from './overview.service';

const initialState: {
  count: number;
  next: string | null;
  previous: string | null;
  filters: OverviewFilters;
  pageSize: number;
  pageIndex: number;
} = {
  count: 0,
  filters: {},
  next: null,
  pageIndex: 0,
  pageSize: 25,
  previous: null,
};
// eslint-disable-next-line @typescript-eslint/naming-convention
export const OverviewStore = signalStore(
  { providedIn: 'root' },
  withDevtools('overviewStore'),
  withState(initialState),
  withEntities<Overview>(),
  withCallState(),
  withProps(() => ({
    overviewService: inject(OverviewService),
    router: inject(Router),
  })),

  withMethods(({ overviewService, ...store }) => ({
    loadOverview: rxMethod<OverviewFilters>(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),

        exhaustMap((filters) => {
          const filtersWithPagination = {
            ...filters,
            limit: store.pageSize(),
            offset: store.pageIndex() * store.pageSize(),
          };

          return overviewService.loadOverview(filtersWithPagination).pipe(
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (response) => {
                patchState(store, setAllEntities(response.results), {
                  count: response.count,
                  filters,
                  next: response.next,
                  previous: response.previous,
                });
              },
            }),
          );
        }),
      ),
    ),

    removeAllEntities: () => {
      patchState(store, removeAllEntities());
    },

    resetPagination: () => {
      patchState(store, { pageIndex: 0 });
    },

    updatePagination: (pageIndex: number, pageSize: number) => {
      patchState(store, { pageIndex, pageSize });
    },
  })),
);
